import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AcademyHeader from '../../../components/LP/AcademyHeader';
import LPFooter from '../../../components/LP/LPFooter';
import SEO from '../../../components/SEO/SEO';

const SeminarLP = () => {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    fetch('/api/admin-read.php?file=seminars&t=' + Date.now())
      .then((r) => r.ok ? r.json().catch(() => []) : [])
      .then((d) => {
        const today = new Date().toISOString().split('T')[0];
        const filtered = (Array.isArray(d) ? d : []).filter((s) => !s.eventDate || s.eventDate >= today);
        setSessions(filtered);
      })
      .catch(() => setSessions([]));
  }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const [formData, setFormData] = useState({ name: '', office: '', email: '', note: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const scrollToSchedule = useCallback(() => {
    const el = document.getElementById('schedule');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const openForm = useCallback((label) => {
    setSelectedSession(label);
    setModalOpen(true);
    setSubmitSuccess(false);
    setFormData({ name: '', office: '', email: '', note: '' });
  }, []);

  const closeForm = useCallback(() => {
    setModalOpen(false);
    setSelectedSession('');
    setFormData({ name: '', office: '', email: '', note: '' });
    setSubmitSuccess(false);
    setIsSubmitting(false);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'seminar',
          session: selectedSession,
          ...formData,
        }),
      });
      setSubmitSuccess(true);
    } catch {
      // handle silently
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedSession, formData]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const [menuOpen, setMenuOpen] = useState(false);


  return (
    <div className="min-h-screen flex flex-col font-noto bg-lp-cream text-lp-charcoal">
      <SEO
        title="SrWith無料プレセミナー"
        description="社労士が『手続きの人』から経営の意思決定に関わる存在へ。無料Zoomセミナー全4回。"
      />

      <AcademyHeader />

      <main className="flex-grow pt-[48px]">
        {/* ===== 1. HERO SECTION ===== */}
        <section className="bg-lp-charcoal pt-20 pb-20 px-4">
          <div className="max-w-[860px] mx-auto text-center">
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              <span className="bg-lp-sage text-white text-sm font-bold px-4 py-1 rounded-full">無料</span>
              <span className="bg-lp-sage text-white text-sm font-bold px-4 py-1 rounded-full">Zoomセミナー</span>
              <span className="bg-lp-sage text-white text-sm font-bold px-4 py-1 rounded-full">全4回</span>
            </div>
            <h1 className="font-noto-serif text-3xl md:text-5xl text-white leading-tight mb-6">
              社労士が『手続きの人』から<br className="hidden md:block" />経営の意思決定に関わる存在へ
            </h1>
            <p className="text-white/70 text-base md:text-lg mb-10 max-w-2xl mx-auto">
              どの回からでも参加できます。1回だけでもOK。セミナー後は希望者のみ個別相談に進めます。
            </p>
            <button
              onClick={scrollToSchedule}
              className="bg-lp-sage hover:bg-lp-sage/90 text-white text-lg font-bold px-8 py-4 rounded-xl transition-colors"
            >
              セミナー一覧・申し込みへ
            </button>
          </div>
        </section>

        {/* ===== 2. META INFO BAR ===== */}
        <section className="bg-white border-y border-lp-border py-4 px-4">
          <div className="max-w-[860px] mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-lp-charcoal-mid">
              <span>Zoom（無料）</span>
              <span>セミナー 60分</span>
              <span>質疑応答 10分</span>
              <span>毎月第2金曜 19:00〜</span>
            </div>
            <div className="flex justify-center mt-3">
              <span className="bg-lp-sage-light text-lp-sage text-xs font-bold px-3 py-1 rounded-full">
                強制勧誘 一切なし
              </span>
            </div>
          </div>
        </section>

        {/* ===== 3. SCHEDULE GRID ===== */}
        <section id="schedule" className="bg-lp-cream py-16 px-4">
          <div className="max-w-[860px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  className={`rounded-xl p-5 flex flex-col items-center text-center ${
                    s.special
                      ? 'bg-lp-sage text-white'
                      : 'bg-white border border-lp-border text-lp-charcoal'
                  }`}
                >
                  <span className={`text-sm font-bold ${s.special ? 'text-white/80' : 'text-lp-sage'}`}>
                    {s.number}
                  </span>
                  <span className="text-lg font-bold mt-1">{s.date}</span>
                  <span className={`text-xs mt-1 ${s.special ? 'text-white/70' : 'text-lp-charcoal-mid'}`}>
                    {s.time}
                  </span>
                  <p className="text-sm font-bold mt-3 mb-3 leading-snug">{s.shortTitle}</p>
                  {s.cardBadge && (
                    <span className={`text-xs px-2 py-0.5 rounded-full mb-3 ${
                      s.special ? 'bg-white/20 text-white' : 'bg-lp-sage-light text-lp-sage'
                    }`}>
                      {s.cardBadge}
                    </span>
                  )}
                  <button
                    onClick={() => openForm(s.label)}
                    className={`mt-auto w-full py-2 rounded-lg text-sm font-bold transition-colors ${
                      s.special
                        ? 'bg-white text-lp-sage hover:bg-white/90'
                        : 'bg-lp-sage text-white hover:bg-lp-sage/90'
                    }`}
                  >
                    申し込む
                  </button>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-lp-charcoal-mid mt-6">
              ※ どの回からでも参加できます。複数回の参加も歓迎です。
            </p>
          </div>
        </section>

        {/* ===== 4. FLOW SECTION ===== */}
        <section className="bg-white py-16 px-4">
          <div className="max-w-[860px] mx-auto">
            <h2 className="font-noto-serif text-2xl md:text-3xl text-center mb-12">当日の流れ</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { part: 'PART 1', time: '0〜60分', title: 'セミナー本編', desc: 'テーマに沿った内容をお届けします' },
                { part: 'PART 2', time: '60〜70分', title: '質疑応答', desc: '疑問をその場で解消できます' },
                { part: 'AFTER', time: '希望者のみ', title: '個別相談（無料）', desc: 'あなたの状況に合わせて一緒に考えます' },
              ].map((step, i) => (
                <div key={i} className="text-center bg-lp-cream rounded-xl p-6">
                  <span className="text-xs font-bold text-lp-sage tracking-wider">{step.part}</span>
                  <span className="block text-sm text-lp-charcoal-mid mt-1 mb-3">{step.time}</span>
                  <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-lp-charcoal-mid">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== 5. SEMINAR DETAILS SECTION ===== */}
        <section className="bg-lp-gray py-16 px-4">
          <div className="max-w-[860px] mx-auto space-y-8">
            {sessions.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl p-6 md:p-8">
                <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-4 ${
                  s.special
                    ? 'bg-lp-sage text-white'
                    : 'bg-lp-sage-light text-lp-sage'
                }`}>
                  {s.badge}
                </span>
                <p className="text-sm text-lp-charcoal-mid mb-2">{s.fullDate}</p>
                <h3 className="font-noto-serif text-xl md:text-2xl font-bold mb-4 leading-snug">
                  {s.fullTitle}
                </h3>
                <p className="text-sm md:text-base text-lp-charcoal-mid mb-6 leading-relaxed">
                  {s.intro}
                </p>
                <ul className="space-y-2 mb-6">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm md:text-base">
                      <span className="text-lp-sage mt-0.5 shrink-0">●</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openForm(s.label)}
                  className="bg-lp-sage hover:bg-lp-sage/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
                >
                  この回に申し込む
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* ===== 6. MID CTA SECTION ===== */}
        <section className="bg-lp-charcoal py-16 px-4">
          <div className="max-w-[860px] mx-auto text-center">
            <h2 className="font-noto-serif text-2xl md:text-3xl text-white mb-4">
              まずは70分、気軽に参加してみてください
            </h2>
            <p className="text-white/70 mb-8">
              強制勧誘は一切なし。どの回からでも、1回だけでも歓迎です。
            </p>
            <button
              onClick={scrollToSchedule}
              className="bg-lp-sage hover:bg-lp-sage/90 text-white text-lg font-bold px-8 py-4 rounded-xl transition-colors"
            >
              参加したい回を選ぶ
            </button>
          </div>
        </section>

        {/* ===== 7. SERIES NAVIGATION ===== */}
        <section className="bg-lp-sage-light py-12 px-4">
          <div className="max-w-[860px] mx-auto">
            <h2 className="font-noto-serif text-xl md:text-2xl text-center mb-8">
              シリーズ全4回 ─ どの回からでも参加できます
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openForm(s.label)}
                  className="bg-white rounded-xl p-4 text-left hover:shadow-md transition-shadow border border-lp-border"
                >
                  <span className="text-sm font-bold text-lp-sage">{s.label}</span>
                  <p className="text-xs text-lp-charcoal-mid mt-1">{s.shortTitle}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ===== 8. MODAL FORM ===== */}
        {modalOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}
          >
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 md:p-8">
              {submitSuccess ? (
                <div className="text-center">
                  <div className="text-5xl mb-4">✉️</div>
                  <h3 className="font-noto-serif text-xl font-bold mb-3">
                    お申し込みありがとうございます
                  </h3>
                  <p className="text-sm text-lp-charcoal-mid mb-6 leading-relaxed">
                    ご登録のメールアドレスにZoom URLをお送りします。当日お会いできることを楽しみにしています！
                  </p>
                  <div className="bg-lp-sage-light rounded-xl p-4 mb-6">
                    <h4 className="font-bold text-sm mb-1">もっと詳しく聞きたい方へ</h4>
                    <p className="text-xs text-lp-charcoal-mid mb-2">
                      あなたの状況に合わせた無料の個別相談も受け付けています。
                    </p>
                    <a
                      href="https://timerex.net/s/shotsu923_b21b/84385a85"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold text-lp-sage hover:underline"
                    >
                      個別相談を予約する（無料）
                    </a>
                  </div>
                  <button
                    onClick={closeForm}
                    className="text-sm text-lp-charcoal-mid hover:text-lp-charcoal transition-colors"
                  >
                    閉じる
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-noto-serif text-xl font-bold text-center mb-4">
                    無料セミナーに申し込む
                  </h3>
                  <div className="flex justify-center mb-6">
                    <span className="bg-lp-sage-light text-lp-sage text-sm font-bold px-4 py-1 rounded-full">
                      {selectedSession}
                    </span>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        お名前 <span className="text-lp-rose">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange('name')}
                        className="w-full border border-lp-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lp-sage focus:border-lp-sage transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1">事務所名</label>
                      <input
                        type="text"
                        value={formData.office}
                        onChange={handleChange('office')}
                        className="w-full border border-lp-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lp-sage focus:border-lp-sage transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        メールアドレス <span className="text-lp-rose">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange('email')}
                        className="w-full border border-lp-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lp-sage focus:border-lp-sage transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1">気になっていること（任意）</label>
                      <textarea
                        value={formData.note}
                        onChange={handleChange('note')}
                        rows={3}
                        className="w-full border border-lp-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lp-sage focus:border-lp-sage transition-colors resize-none"
                      />
                    </div>
                    <div className="text-xs text-lp-charcoal-mid space-y-1 pt-1">
                      <p>※ Zoom URLは申し込み後にメールでお送りします。</p>
                      <p>※ セミナー後の強制勧誘は一切ありません。</p>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-lp-sage hover:bg-lp-sage/90 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors"
                    >
                      {isSubmitting ? '送信中...' : '申し込みを確定する'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      <LPFooter />
    </div>
  );
};

export default SeminarLP;
